import * as React from "react";
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import PhoneTextField from "./PhoneTextField";
import {
  COUNTRY_OPTIONS,
  getCountryById,
  parseStoredPhone,
  toE164Phone,
} from "../utils/phone-utils";
import { PhoneNumberUtil } from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Combined country-code selector + phone number field.
 *
 * @param {string}   value          E.164 phone string (e.g. "+16125551234")
 * @param {function} onChange       Called with an E.164 string on every change
 * @param {boolean}  disabled
 * @param {string}   size           MUI size: "small" | "medium" (default "medium")
 * @param {string}   helperText     Passed through to the phone text field
 */
export default function PhoneInput({ value = "", onChange, disabled = false, size, helperText }) {
  const parsed = React.useMemo(() => parseStoredPhone(value), [value]);
  const [countryId, setCountryId] = React.useState(parsed.countryId);
  const [nationalNumber, setNationalNumber] = React.useState(parsed.nationalDigits);

  React.useEffect(() => {
    const p = parseStoredPhone(value);
    setCountryId(p.countryId);
    setNationalNumber(p.nationalDigits);
  }, [value]);

  const e164 = toE164Phone(countryId, nationalNumber);

  const handleCountryChange = (newCountryId) => {
    setCountryId(newCountryId);
    onChange?.(toE164Phone(newCountryId, nationalNumber));
  };

  const handlePhoneChange = (newE164) => {
    if (!newE164) {
      setNationalNumber("");
      onChange?.("");
      return;
    }
    // Extract the national digits; country is controlled solely by the Select.
    try {
      const number = phoneUtil.parse(newE164, countryId);
      const nat = number.getNationalNumber() ? String(number.getNationalNumber()) : "";
      setNationalNumber(nat);
    } catch (_) {
      // leave nationalNumber unchanged on parse error
    }
    onChange?.(newE164);
  };

  return (
    <Box>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <FormControl sx={{ minWidth: 120, flexShrink: 0 }} size={size} disabled={disabled}>
        <InputLabel id="phone-country-label">Country code</InputLabel>
        <Select
          labelId="phone-country-label"
          label="Country code"
          value={countryId}
          onChange={(e) => handleCountryChange(e.target.value)}
          renderValue={(id) => {
            const country = getCountryById(id);
            const FlagIcon = country.FlagIcon;
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 20,
                    height: 14,
                    display: "inline-flex",
                    overflow: "hidden",
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                >
                  <FlagIcon />
                </Box>
                <span>{country.dialCode}</span>
              </Stack>
            );
          }}
        >
          {COUNTRY_OPTIONS.map((country) => {
            const FlagIcon = country.FlagIcon;
            return (
              <MenuItem key={country.id} value={country.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 20,
                      height: 14,
                      display: "inline-flex",
                      overflow: "hidden",
                      borderRadius: "2px",
                      flexShrink: 0,
                    }}
                  >
                    <FlagIcon />
                  </Box>
                  <span>
                    {country.label} ({country.dialCode})
                  </span>
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <PhoneTextField
        label="Phone number"
        value={e164}
        defaultCountry={countryId}
        nationalOnly
        onChange={handlePhoneChange}
        disabled={disabled}
        size={size}
        fullWidth
      />
    </Stack>
    {helperText && (
      <FormHelperText sx={{ mx: "14px", mt: "3px" }}>{helperText}</FormHelperText>
    )}
    </Box>
  );
}
